import { B, type World } from "./world";

export const PLAYER_W = 0.6;
export const PLAYER_H = 1.8;
export const EYE = 1.62;
export const WALK = 4.3;
export const SPRINT = 5.6;
export const SNEAK = 1.3;
const GRAVITY = 30;
const JUMP_V = 8.6;

export interface Body {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  onGround: boolean;
  inWater: boolean;
  fallStart: number;
}

export interface MoveInput {
  forward: number; // -1..1
  strafe: number; // -1..1
  jump: boolean;
  speed: number;
  yaw: number;
  /** Pocket Edition auto-jump: hop up 1-block steps you walk into. */
  autoJump?: boolean;
}

export type SolidFn = (x: number, y: number, z: number) => boolean;

export function newBody(x: number, y: number, z: number): Body {
  return { x, y, z, vx: 0, vy: 0, vz: 0, onGround: false, inWater: false, fallStart: y };
}

function collides(solid: SolidFn, x: number, y: number, z: number): boolean {
  const h = PLAYER_W / 2;
  const x0 = Math.floor(x - h), x1 = Math.floor(x + h - 1e-6);
  const y0 = Math.floor(y), y1 = Math.floor(y + PLAYER_H - 1e-6);
  const z0 = Math.floor(z - h), z1 = Math.floor(z + h - 1e-6);
  for (let by = y0; by <= y1; by++)
    for (let bz = z0; bz <= z1; bz++)
      for (let bx = x0; bx <= x1; bx++) if (solid(bx, by, bz)) return true;
  return false;
}

/** Lowest height at (x,z), just above y, where the player would fit — the top of what they bumped into. */
function ledgeTop(solid: SolidFn, x: number, z: number, y: number): number | null {
  for (let h = Math.floor(y) + 1; h <= Math.floor(y) + 3; h++) if (!collides(solid, x, h, z)) return h;
  return null;
}

/** Advances a body one step. Returns fall distance on landing (0 otherwise). */
export function step(body: Body, input: MoveInput, dt: number, world: World, solid: SolidFn): number {
  // Stuck inside something (a chest dropped on you, a laggy rubber-band)? Pop up on top of it.
  if (collides(solid, body.x, body.y, body.z)) {
    for (let h = Math.floor(body.y) + 1; h <= Math.floor(body.y) + 3; h++) {
      if (!collides(solid, body.x, h, body.z)) {
        body.y = h;
        body.vy = 0;
        break;
      }
    }
  }
  const feet = world.get(Math.floor(body.x), Math.floor(body.y + 0.4), Math.floor(body.z));
  body.inWater = feet === B.WATER;

  let speed = input.speed * (body.inWater ? 0.55 : 1);
  const len = Math.hypot(input.forward, input.strafe);
  let fx = 0, fz = 0;
  if (len > 0) {
    const f = input.forward / Math.max(1, len);
    const s = input.strafe / Math.max(1, len);
    const sin = Math.sin(input.yaw), cos = Math.cos(input.yaw);
    fx = (-sin * f + cos * s) * speed;
    fz = (-cos * f - sin * s) * speed;
  }
  // Snappy ground control, floaty air control — feels like old MCPE.
  const accel = body.onGround ? 1 : body.inWater ? 0.4 : 0.25;
  const k = Math.min(1, accel * dt * 20);
  body.vx += (fx - body.vx) * k;
  body.vz += (fz - body.vz) * k;

  if (body.inWater) {
    body.vy -= GRAVITY * 0.15 * dt;
    if (input.jump) body.vy = Math.min(body.vy + 30 * dt, 3.2);
    body.vy = Math.max(body.vy, -3);
    body.fallStart = body.y;
  } else {
    body.vy -= GRAVITY * dt;
    if (input.jump && body.onGround) body.vy = JUMP_V;
    body.vy = Math.max(body.vy, -50);
  }

  let fall = 0;
  // Resolve each axis separately against the voxel grid.
  let bump: [number, number] | null = null;
  const nx = body.x + body.vx * dt;
  if (!collides(solid, nx, body.y, body.z)) body.x = nx;
  else {
    body.vx = 0;
    bump = [nx, body.z];
  }

  const nz = body.z + body.vz * dt;
  if (!collides(solid, body.x, body.y, nz)) body.z = nz;
  else {
    body.vz = 0;
    bump ??= [body.x, nz];
  }

  // Walked into something while trying to move: auto-jump onto 1-block steps,
  // and in water, swim up the wall and haul yourself out onto the bank.
  if (bump && len > 0) {
    const top = ledgeTop(solid, bump[0], bump[1], body.y);
    if (body.inWater) {
      const headOut = world.get(Math.floor(body.x), Math.floor(body.y + 1.2), Math.floor(body.z)) !== B.WATER;
      // Just enough hop to clear the lip; floating feet sit below the bank.
      if (headOut && top !== null && top - body.y <= 2.4) body.vy = Math.max(body.vy, Math.sqrt(2 * GRAVITY * (top - body.y + 0.3)));
      else body.vy = Math.max(body.vy, 2.5);
    } else if (input.autoJump && body.onGround && top !== null && top - body.y <= 1.01) {
      body.vy = JUMP_V;
    }
  }

  const ny = body.y + body.vy * dt;
  const wasGround = body.onGround;
  if (!collides(solid, body.x, ny, body.z)) {
    body.y = ny;
    body.onGround = false;
  } else {
    if (body.vy < 0) {
      body.y = Math.floor(ny) + 1;
      if (collides(solid, body.x, body.y, body.z)) body.y = Math.ceil(body.y);
      if (!wasGround && !body.inWater) fall = body.fallStart - body.y;
      body.onGround = true;
    }
    body.vy = 0;
  }
  if (body.onGround || body.vy > 0 || body.inWater) body.fallStart = body.y;
  return fall;
}
